import { create } from 'zustand';

const CURRENCY_STORAGE_KEY = 'selectedCurrency';

export const DEFAULT_USD = {
    currencyCode: 'USD',
    currencyName: 'US Dollar',
    symbol: '$',
    conversionRate: 1,
    countryCode: 'US',
};

/** Shown first in the currency dropdown; all others follow A–Z by code. */
const PRIORITY_CURRENCY_CODES = ['USD', 'CAD', 'GBP', 'EUR', 'SAR', 'CNY'];

const sortCurrencies = (currencies) => {
    const priorityIndex = (code) => {
        const i = PRIORITY_CURRENCY_CODES.indexOf((code || '').toUpperCase());
        return i === -1 ? PRIORITY_CURRENCY_CODES.length : i;
    };

    return [...currencies].sort((a, b) => {
        const aCode = a.currencyCode || '';
        const bCode = b.currencyCode || '';
        const aPri = priorityIndex(aCode);
        const bPri = priorityIndex(bCode);

        if (aPri !== bPri) return aPri - bPri;
        return aCode.localeCompare(bCode);
    });
};

const normalizeCurrency = (currency) => {
    if (!currency) return { ...DEFAULT_USD };

    return {
        countryCode: currency.countryCode || currency.CountryCode || null,
        currencyCode: currency.currencyCode || currency.CurrencyCode || DEFAULT_USD.currencyCode,
        currencyName: currency.currencyName || currency.CurrencyName || DEFAULT_USD.currencyName,
        symbol: currency.symbol || currency.Symbol || DEFAULT_USD.symbol,
        conversionRate: Number(
            currency.conversionRate ?? currency.ConversionRate ?? DEFAULT_USD.conversionRate
        ) || 1,
    };
};

const loadStoredCurrency = () => {
    try {
        const stored = sessionStorage.getItem(CURRENCY_STORAGE_KEY);
        if (stored) {
            return normalizeCurrency(JSON.parse(stored));
        }
    } catch (error) {
        console.error('Error parsing stored currency:', error);
        sessionStorage.removeItem(CURRENCY_STORAGE_KEY);
    }
    return { ...DEFAULT_USD };
};

const currencyStore = (set, get) => ({
    selectedCurrency: loadStoredCurrency(),
    availableCurrencies: [DEFAULT_USD],
    isLoadingCurrencies: false,

    setAvailableCurrencies: (currencies) => {
        const list = Array.isArray(currencies) && currencies.length > 0
            ? currencies.map(normalizeCurrency)
            : [DEFAULT_USD];

        // Ensure USD is always present
        if (!list.some((c) => c.currencyCode === 'USD')) {
            list.unshift({ ...DEFAULT_USD });
        }

        set({ availableCurrencies: sortCurrencies(list) });
    },

    setSelectedCurrency: (currency) => {
        const normalized = normalizeCurrency(currency);
        sessionStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(normalized));
        set({ selectedCurrency: normalized });
    },

    setCurrencyByCode: (currencyCode) => {
        const { availableCurrencies, setSelectedCurrency } = get();
        const match = availableCurrencies.find(
            (c) => c.currencyCode?.toUpperCase() === currencyCode?.toUpperCase()
        );
        setSelectedCurrency(match || DEFAULT_USD);
    },

    resetToUsd: () => {
        get().setSelectedCurrency(DEFAULT_USD);
    },
});

const useCurrencyStore = create(currencyStore);
export default useCurrencyStore;
